import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { PusherEventName } from 'anchor-scout-common/types';
import { Button, Callout, Typography, useIsMounted, useOverlayContext } from 'seagull';
import { useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { getAccountingDetails } from '~/integrations/Accounting/accounting.utils';
import { accountingIconMap } from '~/integrations/Accounting/AccountingIcon/accountingIconMap';
import { timeModel } from '~/models';
import { PayoutIntegrationModel } from '~/models/PayoutIntegrationModel';
import { useBusinessChannelEventBinder } from '~/pusher/useBusinessChannelEventBinder';
import { Accounting } from '~/types/api.model';
import { getPayoutAccountingErrorMessage } from '~/utils/getAccountingInvoiceErrorMessage';
import styles from './PayoutAccountingSection.module.scss';
function getAccountingInfoStatus(payoutIntegration) {
    return payoutIntegration.accept({
        whenPendingSync: ()=>'pending',
        whenSynced: ()=>'success',
        whenSyncFailed: ()=>'error',
        whenSyncInProgress: ()=>'syncing',
        whenDeleted: ()=>'success'
    });
}
function getCalloutIntent(status) {
    return status === 'error' ? 'error' : 'info';
}
const accountingFullName = (accounting)=>getAccountingDetails(accounting).fullName;
const pusherEventNameByAccounting = {
    [Accounting.QBO]: PusherEventName.PayoutQboIntegrationStatusUpdated,
    [Accounting.Xero]: PusherEventName.PayoutXeroIntegrationStatusUpdated
};
export function getInfo(param) {
    let { payoutIntegration, accounting } = param;
    return payoutIntegration.accept({
        whenPendingSync: ()=>"Payout is ready to sync with ".concat(accountingFullName(accounting), "."),
        whenSyncInProgress: ()=>'Sync in progress.',
        whenSynced: ()=>null,
        whenSyncFailed: ()=>getPayoutAccountingErrorMessage(payoutIntegration, accounting),
        whenDeleted: (param)=>{
            let { date } = param;
            return "Payout was deleted from ".concat(accountingFullName(accounting), " on ").concat(timeModel.formatDateWithTime(date), ".");
        }
    });
}
export default function PayoutAccountingSection(props) {
    const { payoutId, payoutIntegration, testId, accounting } = props;
    const isMounted = useIsMounted();
    const { showSuccessToast, showErrorToast, hideBottomSheet } = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { t: payoutsTexts } = useLocaleKeys('pages.payoutsDashboard');
    const { api } = useStoreActions();
    const [isSyncing, setIsSyncing] = useState(false);
    const status = getAccountingInfoStatus(payoutIntegration);
    const info = getInfo({
        payoutIntegration,
        accounting
    });
    const { startListening } = useBusinessChannelEventBinder(pusherEventNameByAccounting[accounting], 'scout');
    async function syncPayout() {
        setIsSyncing(true);
        startListening((event)=>{
            if (event.payoutId !== payoutId) return;
            const match = event.integrations.find((i)=>i.type === accounting);
            if (!match) return;
            const updatedIntegration = PayoutIntegrationModel.fromDTO(match.value);
            if (updatedIntegration.isSyncFailed()) {
                setIsSyncing(false);
                hideBottomSheet();
                openSyncToast('failure');
            } else if (updatedIntegration.isSynced()) {
                setIsSyncing(false);
                hideBottomSheet();
                openSyncToast('success');
            }
        });
        const { success } = await handleRequest(api.accounting.syncPayout({
            accounting,
            payoutId
        }));
        if (!isMounted()) return;
        if (!success) {
            setIsSyncing(false);
            openSyncToast('failure');
        }
    }
    function openSyncToast(type) {
        if (type === 'success') {
            showSuccessToast(commonTexts.toasts.payoutSuccessfullySynced(), 'sync-success-toast');
        } else {
            showErrorToast(commonTexts.toasts.failedToSyncPayout(), 'sync-failure-toast');
        }
    }
    if (status === 'success') {
        return null;
    }
    return /*#__PURE__*/ _jsx(Callout, {
        intent: getCalloutIntent(status),
        icon: accountingIconMap[accounting][status],
        withBorder: true,
        hideIcon: false,
        testId: testId,
        "data-status": status,
        classes: {
            wrapper: styles.callout
        },
        withAction: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                payoutIntegration.isSyncFailed() && /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'retry_payout_accounting_sync'
                    },
                    className: styles.retrySyncBtn,
                    skin: "primaryOutline",
                    size: "small",
                    testId: "retry-sync-payout",
                    loading: isSyncing,
                    loadingText: payoutsTexts.syncing(),
                    onClick: ()=>syncPayout(),
                    children: payoutsTexts.retrySync()
                }),
                payoutIntegration.isPendingSync() && /*#__PURE__*/ _jsx(Button, {
                    bi: {
                        name: 'sync_payout_to_accounting'
                    },
                    testId: "sync-payout",
                    onClick: ()=>syncPayout(),
                    skin: "primaryOutline",
                    size: "small",
                    loading: isSyncing,
                    loadingText: payoutsTexts.syncing(),
                    children: "Sync"
                })
            ]
        }),
        children: /*#__PURE__*/ _jsx(Typography, {
            testId: testId && "".concat(testId, "-info"),
            children: info
        })
    });
}
